// Client-side validation of LLM-generated HTML before it's shown or saved.
// Renders in a hidden sandboxed iframe (same sandbox attrs used everywhere
// else — allow-scripts only, no allow-same-origin) and watches for parse
// failure, console errors, or structural defects (stuck overlays, content
// hidden by default) so the caller can retry with the specific violation
// named. See functions/systemPrompt.js's "STRUCTURAL CHECKLIST" for the
// prompt-side half of this — these checks exist because that class of bug
// throws no JS error and would otherwise sail through undetected.

export function isWellFormedHtml(html) {
  if (!html || typeof html !== 'string' || html.trim().length === 0) return false;
  if (!/<script[\s>]/i.test(html)) return false; // spec requires interactivity
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const parserError = doc.querySelector('parsererror');
  return !parserError && !!doc.body && doc.body.childElementCount > 0;
}

export function inspectGeneratedHtml(html) {
  const problems = [];
  const source = String(html || '');
  const withoutBaseStyles = source
    .replace(/<!--LL_BASE_STYLES_START-->[\s\S]*?<!--LL_BASE_STYLES_END-->/g, '')
    // Same idea for the injected domain-library block (see
    // functions/baseScripts.js) — e.g. LLCircuit for electrical/power
    // topics. It's platform-injected, version-pinned code, not something
    // the model wrote, so it must not count toward "real content" size/
    // structure heuristics below any more than the base CSS does.
    .replace(/<!--LL_BASE_SCRIPTS_START-->[\s\S]*?<!--LL_BASE_SCRIPTS_END-->/g, '')
    .trim();
  const lower = source.toLowerCase();
  const contentText = withoutBaseStyles
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const forbidden = [
    'placeholder for',
    'placeholder logic',
    'todo',
    'coming soon',
    'not implemented',
    '3d rendering logic',
  ];

  for (const phrase of forbidden) {
    if (lower.includes(phrase)) {
      problems.push(`Generated HTML contains unfinished placeholder text: "${phrase}".`);
      break;
    }
  }

  if (withoutBaseStyles.length < 1200) {
    problems.push('Generated HTML is too small after removing platform base styles; it appears to contain no real page content.');
  }
  if (!/<!doctype html/i.test(withoutBaseStyles) || !/<html[\s>]/i.test(withoutBaseStyles) || !/<\/html>/i.test(withoutBaseStyles)) {
    problems.push('Generated HTML is not a complete document from <!doctype html> through </html>.');
  }
  if (!/<body[\s>]/i.test(withoutBaseStyles) || !/<script[\s>]/i.test(withoutBaseStyles)) {
    problems.push('Generated HTML must include a real <body> and a <script> block.');
  }
  if (contentText.length < 120 && !/<canvas[\s>]/i.test(withoutBaseStyles)) {
    problems.push('Generated HTML has too little visible learner-facing content.');
  }

  // The only sanctioned way to reach any backend is window.askAI() (see
  // functions/systemPrompt.js) — the sandboxed iframe has no session/token
  // to make a real network call work anyway, so a stray fetch()/XHR/
  // WebSocket almost certainly means the model ignored that instruction
  // and wrote dead (or worse, silently-failing) code instead of using the
  // bridge or just writing the content directly into the page.
  const scriptBodies = (withoutBaseStyles.match(/<script[^>]*>([\s\S]*?)<\/script>/gi) || []).join('\n');
  if (/\bfetch\s*\(/.test(scriptBodies) || /\bXMLHttpRequest\b/.test(scriptBodies) || /\bnew\s+WebSocket\s*\(/.test(scriptBodies)) {
    problems.push('Generated HTML makes a raw network call (fetch/XMLHttpRequest/WebSocket) other than the built-in window.askAI() bridge — this will not work inside the sandboxed preview. Use window.askAI(question) for AI interaction, or write the content directly into the page instead of fetching it.');
  }

  const doc = new DOMParser().parseFromString(source, 'text/html');
  const canvasCount = doc.querySelectorAll('canvas').length;
  const nestedCardsWithCanvas = doc.querySelectorAll('.card .card canvas, .card .card .ll-canvas-wrap').length;
  const broadHiddenCardRule = /\.card\s*\{[^}]*display\s*:\s*none/i.test(source);
  if (canvasCount > 0 && broadHiddenCardRule && nestedCardsWithCanvas > 0) {
    problems.push('Canvas content is inside nested .card elements while a broad ".card { display: none }" rule hides all cards by default. Use separate classes for panels versus visible content cards, or make nested content cards visible.');
  }

  const broadTabCardToggle = /querySelectorAll\(['"]\.card['"]\)/.test(source)
    && /classList\.(?:add|remove)\(['"]active['"]\)/.test(source)
    && nestedCardsWithCanvas > 0;
  if (broadTabCardToggle) {
    problems.push('Tab logic targets every ".card" on the page, including nested content cards. Scope tab switching to top-level panels only, so transformation cards and canvases remain visible.');
  }

  const has3dCanvas = /id=["'][^"']*3d[^"']*["'][^>]*canvas|canvas[^>]*id=["'][^"']*3d[^"']*["']/i.test(source);
  const hasFlat3dRect = /function\s+\w*3d\w*\s*\([^)]*\)\s*\{[\s\S]{0,900}fillRect\s*\(/i.test(source)
    || /id=["']3dCanvas["'][\s\S]{0,1800}fillRect\s*\(/i.test(source);
  const hasProjection = /project\w*\s*\(|perspective|vertices|edges|rotate[XYZ]|z\s*:/i.test(source);
  if (has3dCanvas && hasFlat3dRect && !hasProjection) {
    problems.push('The 3D view appears to draw only a flat rectangle. Implement real projected 3D geometry with vertices, edges, perspective/camera rotation, and visible x/y/z axes.');
  }

  // ── Animation-runtime adoption checks ───────────────────────────────
  // When the platform injected SimKit (see functions/baseScripts.js) the
  // model was explicitly instructed to drive animation through it. A page
  // that hand-rolls raw requestAnimationFrame while SimKit sits unused
  // loses the fixed-timestep/pause/speed/dt-clamping guarantees — flag it
  // so the retry adopts the runtime instead. Only fires when the library
  // is actually on the page AND the page animates.
  const simKitInjected = /Learning Lab base library: window\.SimKit/.test(source);
  const ownScriptBodies = (withoutBaseStyles.match(/<script[^>]*>([\s\S]*?)<\/script>/gi) || []).join('\n');
  if (simKitInjected && /requestAnimationFrame\s*\(/.test(ownScriptBodies) && !/\bSimKit\s*[.(]/.test(ownScriptBodies)) {
    problems.push('The SimKit animation runtime is pre-loaded on this page but completely unused, while the page hand-rolls its own requestAnimationFrame loop. Drive the animation with `new SimKit.Loop({ update(dt){...}, render(t){...} })` (plus loop.toggle()/setSpeed() for the play/pause/speed controls) instead of a raw requestAnimationFrame loop.');
  }
  // setInterval-driven animation: a fast interval redrawing a canvas is a
  // classic jank source (fires off the display's refresh cadence, keeps
  // running in hidden tabs). Only flag clearly animation-rate intervals.
  const fastInterval = ownScriptBodies.match(/setInterval\s*\(\s*[^,]+,\s*(\d{1,2})\s*\)/);
  if (fastInterval && Number(fastInterval[1]) < 60 && /<canvas[\s>]/i.test(withoutBaseStyles)) {
    problems.push(`Animation is driven by setInterval(..., ${fastInterval[1]}ms). Use requestAnimationFrame${simKitInjected ? ' (via SimKit.Loop, which is already loaded on this page)' : ''} so frames sync to the display and pause correctly in background tabs.`);
  }

  const mutatesCanvasTransform = /\.(translate|rotate|scale|transform)\s*\(/.test(source);
  const hasAnimationLoop = /requestAnimationFrame\s*\(/.test(source);
  const hasDurableState = /\b(state|model|transformState|translation|rotation|scaleFactor|shear)\s*=\s*\{/.test(source)
    || /\b(let|const)\s+(tx|ty|angle|scale|shearX|shearY)\b/.test(source);
  if (canvasCount > 0 && hasAnimationLoop && mutatesCanvasTransform && !hasDurableState) {
    problems.push('Canvas interactions mutate the drawing context directly instead of durable state. Store transformation parameters in state variables and redraw from state each frame.');
  }

  return problems;
}

// Browser noise that arrives through window.onerror but says nothing about
// the generated page. Filtered out before it can reach the repair loop,
// because a message that names no defect cannot be repaired: the model is
// handed "fix this" with nothing to fix, rewrites the page at random, and
// the same warning fires again on the next attempt. That burns both
// retries and then fails a page that was very likely fine.
//
// ResizeObserver is the notorious one, and it fires for a mundane reason
// here: probePage deliberately resizes the iframe mid-render (desktop →
// 375px phone width, see below) to audit the responsive layout. Any page
// observing its own container — every canvas that resizes to fit, i.e.
// most simulations — legitimately reflows in response, and Chrome reports
// "loop completed with undelivered notifications" whenever that reflow
// doesn't settle inside one frame. It is a warning that the observer
// callback was rescheduled, not an error, and the spec explicitly permits
// it. Adding an entry here should always require this much justification:
// silencing a real defect class is far more expensive than a noisy retry.
const BENIGN_RENDER_ERRORS = [
  /ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/i,
  // Cross-origin script errors are opaque by design: no message, no line,
  // no file. There is nothing for a repair prompt to act on.
  /^Script error\.?$/i,
];

export function isBenignRenderError(message) {
  const text = String(message || '').trim();
  return BENIGN_RENDER_ERRORS.some((re) => re.test(text));
}

// A script that throws during initialization never gets as far as drawing
// anything, so the visual checks downstream of it all fire at once: blank
// canvas, hidden sections, NaN readouts, frozen animation. They are one
// fault wearing five hats.
//
// Reporting all five is actively harmful, not merely verbose. The list
// becomes a numbered repair prompt, and a model handed five items treats
// them as five independent faults — the observed failure mode is that it
// "fixes" the symptoms (forces `display:block` on the hidden section, hard-
// codes a number where the NaN was) while the ReferenceError that caused
// every one of them survives untouched. Then the retry budget is gone.
//
// So when a hard runtime error is present, the symptoms it explains are
// dropped and only the cause is sent. Nothing is lost by this: the loop
// re-validates from scratch after every attempt, so any symptom that
// genuinely has its own cause simply surfaces on the next pass, once the
// page can actually run. Structural findings that a runtime error CANNOT
// explain (placeholder text, a document too small, missing <body>) are
// never suppressed — they're independent evidence.
const HARD_RUNTIME_ERROR = /ReferenceError|TypeError|SyntaxError|syntax error|is not defined|cannot read|can't access|before initialization|Unhandled promise rejection/i;

const CONSEQUENCE_OF_DEAD_SCRIPT = [
  /none are visible in the default view/i,
  /appears? blank/i,
  /hidden by default/i,
  /invalid runtime result/i,
  /zero drawing size/i,
  /never changes over time|appears frozen/i,
  /have no click\/input\/change handler/i,
  /below a smooth frame rate|roughly \d+ fps/i,
  /layout-shift score/i,
  /overflows horizontally/i,
  /insufficient foreground\/background contrast/i,
  /has no visible score|has no working reset/i,
];

// ── What is actually allowed to fail a generation ───────────────────────
//
// THE PRODUCT RULE, stated once so the list below is readable as a
// consequence rather than a pile of exceptions:
//
//     If the simulation or animation works, ship the page.
//
// Not "if the page is perfect". A layout that stacks awkwardly on a phone,
// a label sitting slightly wrong, a quiz that scores oddly — none of those
// are worth throwing away a working simulation the learner is waiting for,
// and all of them are things they can see and ask to fix in one more turn.
// A simulation that doesn't run is different in kind: there is nothing to
// look at and nothing to react to.
//
// So this list is deliberately narrow, and every entry answers yes to one
// question: does this mean the page fails to run, or the simulation fails
// to appear, move, or respond? Everything else is recorded and shipped.
//
// The arithmetic that forces this: two retries are the entire budget. A
// page spending both on a contrast ratio is a page nobody receives. And
// the demoted checks are also the least trustworthy in this file — the
// contrast test guesses each element's background by climbing parents, so
// it misreads text over a gradient or a canvas, which is most of what this
// platform generates.
//
// NOTE ON ADDING CHECKS: a new check is report-only unless its message is
// matched here. That is the opposite of the usual "fail safe" default and
// it is a deliberate product choice — when in doubt, the learner gets the
// page. If you add a check that genuinely means the simulation is dead,
// add its pattern here and give it a test.
const BLOCKING_PROBLEMS = [
  // The document itself is broken — nothing will render.
  /not well-formed HTML/i,
  /not a complete document/i,
  /must include a real <body>/i,
  /too small after removing platform base styles/i,
  /script syntax error|syntax error/i,
  // The script died, so nothing runs.
  /ReferenceError|TypeError|SyntaxError|is not defined|cannot read|can't access|before initialization|Unhandled promise rejection|Control failed:/i,
  // The model shipped a stub instead of the thing.
  /unfinished placeholder text|not implemented/i,
  // The simulation is absent, invisible, blank, or frozen.
  /none are visible in the default view/i,
  /appears? blank/i,
  /zero drawing size/i,
  /never changes over time|appears frozen/i,
  // It runs, but far too slowly to watch.
  /below a smooth frame rate|roughly \d+ fps/i,
  // The controls that drive it do nothing.
  /have no click\/input\/change handler/i,
  // It is producing garbage instead of numbers.
  /invalid runtime result/i,
  // Something is covering the whole page with no way past it.
  /full-page overlay/i,
];

/**
 * partitionProblems — splits findings into what may fail a generation and
 * what is merely recorded.
 *
 * @returns {{blocking: string[], cosmetic: string[]}}
 */
export function partitionProblems(problems) {
  const list = Array.isArray(problems) ? problems.map((p) => String(p)) : [];
  const isBlocking = (p) => BLOCKING_PROBLEMS.some((re) => re.test(p));
  return { blocking: list.filter(isBlocking), cosmetic: list.filter((p) => !isBlocking(p)) };
}

/**
 * focusProblems — given everything wrong with a page, return what the repair
 * prompt should actually name. Pure and exported so its behaviour is pinned
 * by tests rather than discovered in production.
 */
export function focusProblems(problems) {
  const list = Array.isArray(problems) ? problems.map((p) => String(p)) : [];
  if (!list.some((p) => HARD_RUNTIME_ERROR.test(p))) return list;
  return list.filter((p) => !CONSEQUENCE_OF_DEAD_SCRIPT.some((re) => re.test(p)));
}

// Renders `html` in an offscreen sandboxed iframe and resolves with any
// console errors or structural-defect messages captured within `timeoutMs`.
// Never grants allow-same-origin, so even a broken/malicious generation
// can't read cookies, storage, or reach the parent Firebase session
// (see README "Sandboxing").
//
// Thin wrapper over probePage (below), which does the actual render. Kept
// with its original name and array return type because three callers rely
// on it (src/lib/generate.js, src/views/studio.js's publish check,
// src/views/marketplace.js) and none of them wants the observation record.
// These are actual CSS viewport dimensions, rather than visual scale
// factors. They cover the phones, tablet, and laptop layouts we promise to
// support in both the browser and Android WebView.
export const RESPONSIVE_VIEWPORTS = Object.freeze([
  Object.freeze({ id: 'phone-360', label: 'Phone 360', width: 360, height: 800 }),
  Object.freeze({ id: 'phone-390', label: 'Phone 390', width: 390, height: 844 }),
  Object.freeze({ id: 'phone-412', label: 'Phone 412', width: 412, height: 915 }),
  Object.freeze({ id: 'tablet-768', label: 'Tablet 768', width: 768, height: 1024 }),
  Object.freeze({ id: 'laptop-1366', label: 'Laptop 1366', width: 1366, height: 768 }),
]);

/** Run isolated validation frames in parallel, one at each exact viewport. */
export async function testResponsiveHtml(html, timeoutMs = 2200, viewports = RESPONSIVE_VIEWPORTS) {
  return Promise.all(viewports.map(async (viewport) => ({
    viewport,
    ...(await probePage(html, timeoutMs, { viewport })),
  })));
}

// The same defect in every frame is one defect, not five. A defect which
// only occurs on selected screens keeps those screen names for a useful fix.
export function summarizeResponsiveProblems(checks) {
  const grouped = new Map();
  for (const check of checks || []) {
    for (const problem of check.problems || []) {
      const key = String(problem);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(check.viewport?.label || 'Unknown viewport');
    }
  }
  return [...grouped.entries()].map(([problem, labels]) => {
    const screens = [...new Set(labels)];
    return `${screens.length === RESPONSIVE_VIEWPORTS.length ? 'All tested screens' : screens.join(', ')}: ${problem}`;
  });
}

// Generation/publishing callers retain their small array API, while all new
// HTML is now tested across the complete responsive device matrix.
export async function testRenderForErrors(html, timeoutMs = 2200) {
  return summarizeResponsiveProblems(await testResponsiveHtml(html, timeoutMs));
}

/**
 * probePage — the same hidden-iframe render, but it also KEEPS what it saw
 * instead of only reporting what was wrong with it.
 *
 * WHY: every check in this file answers "is this page broken?". None of
 * them can answer "did this page lose something it had last version?",
 * because nothing was ever written down about the last version. The
 * observation record returned here is that missing memory — see
 * src/lib/pageDiff.js for the comparison and functions/AGENT_BRIEF.md's
 * Phase B for the reasoning. Crucially this needs NO new infrastructure:
 * the browser was already here, it was just throwing its notes away.
 *
 * The record is deliberately coarse — booleans and small counts, never
 * pixel checksums or screenshots. Checksums are not comparable across two
 * separately-generated pages, and a record that notices everything reports
 * a regression every turn.
 *
 * `null` in any observation means COULD NOT TELL, and pageDiff.js treats it
 * as "no finding". That distinction is load-bearing: a control whose effect
 * we cannot observe (because every canvas on the page is animating anyway,
 * or because its handler redraws on the next frame rather than
 * synchronously) must never be reported as broken.
 *
 * @returns {Promise<{problems: string[], observations: object|null}>}
 */
export function probePage(html, timeoutMs = 2600, { viewport = null } = {}) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('sandbox', 'allow-scripts');
    const width = Number(viewport?.width) || 800;
    const height = Number(viewport?.height) || 600;
    iframe.style.cssText = `position:fixed;left:-9999px;top:-9999px;width:${width}px;height:${height}px;`;
    const errors = [];
    let observations = null;

    const onMessage = (event) => {
      if (event.data && event.data.__llErrorReport) {
        // Benign browser noise is dropped, and an error that fires on every
        // frame of an animation loop is recorded ONCE. Both matter for the
        // same reason: the message list becomes a numbered repair prompt,
        // and three identical lines read to the model as three separate
        // faults to chase.
        const message = event.data.message;
        if (!isBenignRenderError(message) && !errors.includes(message)) errors.push(message);
      }
      if (event.data && event.data.__llVisualReport) {
        const report = event.data;
        observations = report.observations || null;
        if (report.canvasCount > 0 && report.visibleCanvasCount === 0) {
          errors.push('The page contains canvases, but none are visible in the default view.');
        }
        if (report.visibleCanvasCount > 0 && report.paintedCanvasCount === 0) {
          errors.push('Visible canvases appear blank after rendering; draw the diagram/simulation before saving.');
        }
        if (report.zeroSizeCanvasCount > 0) {
          errors.push('One or more canvases have zero drawing size; set explicit canvas width and height before drawing.');
        }
        if (report.blockingOverlayCount > 0) {
          errors.push('A full-page overlay/backdrop element is visible on load with no close control and is blocking the content underneath. Overlays must start hidden and only appear after a real user action, with a working close/dismiss control.');
        }
        if (report.hiddenTopLevelCount > 0) {
          errors.push('One or more top-level content sections are hidden by default (display:none, visibility:hidden, or zero size) with no visible way to reveal them. The default/first view must show its content immediately without requiring a click.');
        }
        if (report.frozenAnimatedCanvasCount > 0) {
          errors.push('One or more canvases call requestAnimationFrame repeatedly but their pixel content never changes over time — the animation appears frozen on a single frame. Verify the animation loop actually mutates state and redraws visibly-different content every frame (not just re-drawing the identical frame, and not stuck because a condition never becomes true).');
        }
        // Performance gate: a continuously-animating page that can't hold a
        // reasonable frame rate on the validating machine ships as jank.
        // Threshold is deliberately conservative (well below 60) and only
        // applies when the loop is genuinely continuous and this tab is
        // actually visible (background tabs throttle rAF legitimately).
        if (report.fpsEstimate != null && report.fpsFrames >= 10 && report.fpsEstimate < 24 && !document.hidden) {
          errors.push(`The animation runs at roughly ${Math.round(report.fpsEstimate)} fps — far below a smooth frame rate. Optimize the render loop: cap or pool particles, avoid shadowBlur/gradients recreated over large areas every frame, do not allocate objects or query the DOM inside the loop, and draw static backgrounds once to an offscreen canvas instead of redrawing them every frame.`);
        }
        if (report.inertControlCount > 0) {
          errors.push(`${report.inertControlCount} visible button/control(s) appear to have no click/input/change handler or native form action.`);
        }
        if (report.undersizedTouchTargetCount > 0) {
          errors.push(`${report.undersizedTouchTargetCount} primary touch control(s) are smaller than 36 CSS pixels on a phone viewport. Increase their hit area toward 44×44px without shrinking the visible label.`);
        }
        if (report.lowContrastCount > 0) {
          errors.push(`${report.lowContrastCount} visible text element(s) have insufficient foreground/background contrast.`);
        }
        if (report.desktopOverflowCount > 0 || report.mobileOverflowCount > 0) {
          errors.push(`Page overflows horizontally (${report.desktopOverflowCount || 0} desktop, ${report.mobileOverflowCount || 0} phone element(s)). Make the layout responsive without clipping.`);
        }
        if (report.layoutShiftScore > 0.1) {
          errors.push(`Changing runtime content causes visible layout movement (layout-shift score ${report.layoutShiftScore.toFixed(3)}). Reserve space for the longest state before first render.`);
        }
        if (report.invalidOutputCount > 0) {
          errors.push('Simulation displays an invalid runtime result (NaN, undefined, or Infinity). Correct the formulas, limits, and initial state.');
        }
        if (report.quizMissingScore) errors.push('Quiz-like page has no visible score/progress output.');
        if (report.quizMissingReset) errors.push('Quiz-like page has no working reset/restart/try-again control.');
      }
    };
    window.addEventListener('message', onMessage);

    const instrumented = html.replace('</head>', `${buildInstrumentationScript(timeoutMs, {
      responsiveResizeAudit: !viewport,
      touchTargetAudit: !!viewport && width <= 412,
    })}</head>`);

    iframe.srcdoc = instrumented.includes('</head>') ? instrumented : html;
    document.body.appendChild(iframe);

    // Audit once at desktop width and once at a narrow-phone width — then
    // GO BACK to desktop before the page is judged.
    //
    // This restore was missing, and it quietly invalidated every visibility
    // check in the final report. The frame narrowed to 375px at 0.48×timeout
    // and stayed there, so "canvases exist but none are visible" and "top-
    // level sections are hidden by default" were both being decided against
    // a phone layout. Any ordinary responsive rule — a controls column or
    // side panel that collapses under a mobile breakpoint, a canvas in a
    // grid track that stacks away — reads as a hidden section, and the page
    // fails validation for being correctly responsive. Only overflow
    // legitimately cares about width, and it is now sampled at both.
    // A responsive audit gets a fresh frame at each named viewport; it must
    // remain at that size for its final measurements to be meaningful.
    if (!viewport) {
      setTimeout(() => { iframe.style.width = '375px'; }, Math.max(500, Math.floor(timeoutMs * 0.48)));
      setTimeout(() => { iframe.style.width = '800px'; }, Math.max(700, Math.floor(timeoutMs * 0.66)));
    }

    setTimeout(() => {
      window.removeEventListener('message', onMessage);
      document.body.removeChild(iframe);
      // observations stays null when the page never got as far as posting
      // its report (a parse failure, a script that hangs the frame, a
      // timeout). pageDiff.js reads null as "nothing to compare" and
      // reports nothing — fail-open, like every other gate here.
      resolve({ problems: errors, observations });
    }, timeoutMs);
  });
}

/**
 * The instrumentation script spliced into the head of every probed page.
 *
 * Extracted from probePage into its own exported function for one reason:
 * a bug inside this string is a bug in code that only ever runs inside a
 * sandboxed iframe, where it fails silently and simply produces no report.
 * As its own function it can be handed to `new Function()` by
 * scripts/checkPageInventory.mjs and parse-checked in CI — the same trick
 * scripts/checkLayoutEditor.mjs uses on src/lib/layoutEditor.js's injected
 * bridge, and for the same reason: building the outer module cannot catch
 * an escaping mistake inside a template literal.
 */
export function buildInstrumentationScript(timeoutMs = 2600, { responsiveResizeAudit = true, touchTargetAudit = false } = {}) {
  return `<script>
        // ── Deterministic randomness ──────────────────────────────────
        // NON-NEGOTIABLE for cross-version comparison, and the reason it
        // sits at the very top of this shim: SimKit.rand/randSign (see
        // functions/libs/) call bare Math.random, so a page seeded
        // differently on every render produces a different canvas every
        // time. Against a stochastic scene, "the canvas changed when I
        // moved the slider" and "canvas 1 animated" are both coin flips,
        // and every regression finding downstream would be noise.
        // mulberry32 — 4 lines, uniform enough for a visual probe, and
        // identical on every render because the seed is a constant.
        (function(){
          var __s = 0x9e3779b9;
          Math.random = function(){
            __s |= 0; __s = (__s + 0x6d2b79f5) | 0;
            var t = Math.imul(__s ^ (__s >>> 15), 1 | __s);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
          };
        })();

        // Errors carry their line number so the repair retry can point the
        // model at WHERE it broke, not just what the message said. Promise
        // rejections are captured too — an async/await failure (a rejected
        // askAI call path, a bad dynamic import) throws no window.onerror.
        window.onerror=function(m,s,l){parent.postMessage({__llErrorReport:true,message:String(m)+(l?' (at line '+l+')':'')},'*')};
        window.addEventListener('unhandledrejection',function(e){
          var r=e&&e.reason;var msg=r&&(r.message||r.toString&&r.toString())||'unknown reason';
          parent.postMessage({__llErrorReport:true,message:'Unhandled promise rejection: '+String(msg)},'*');
        });
        // This validation render never wires up the real Ask-AI bridge
        // (see lib/askAI.js) — no point spending a real AI call on a
        // throwaway, 1.5s hidden check. A stub that just never resolves
        // is enough to make sure a compliant page (which only calls this
        // from a user action anyway) doesn't throw "askAI is not a
        // function" and fail validation for the wrong reason.
        window.askAI = window.askAI || function(){ return new Promise(function(){}); };
        // Same for the structured sibling (see lib/askAI.js's
        // injectAskAIBridge) — a comprehension/quiz page calls this one
        // instead, and without a stub it would fail validation with
        // "askAIContent is not a function" rather than for any real defect.
        window.askAIContent = window.askAIContent || function(){ return new Promise(function(){}); };

        // Record event wiring installed by the generated page. This catches
        // controls that look interactive but have no direct or delegated
        // click/input/change behavior. Installed before body-end scripts run.
        var __llListeners = [];
        // Wiring types that count as "this control does something". Used to
        // ONLY include click/input/change/submit/pointer*/touch* — a custom
        // drag control (an angle handle, a role="slider" drag box built with
        // plain mousedown/mousemove/mouseup rather than the newer Pointer
        // Events API — see e.g. functions/libs/uiKit.js's before/after
        // slider) was invisible to this list entirely, so a genuinely-wired
        // control read as inert just because of WHICH event API it used to
        // wire itself, not because it was actually broken.
        var __llWiredTypes = /^(click|input|change|submit|dblclick|keydown|keyup|mousedown|mouseup|mousemove)$/;
        var __llOriginalAdd = EventTarget.prototype.addEventListener;
        EventTarget.prototype.addEventListener = function(type, listener, options) {
          if (__llWiredTypes.test(type) || type.indexOf('pointer') === 0 || type.indexOf('touch') === 0) {
            __llListeners.push({ target: this, type: type });
          }
          return __llOriginalAdd.call(this, type, listener, options);
        };
        var __llShiftScore = 0;
        try {
          new PerformanceObserver(function(list) {
            list.getEntries().forEach(function(entry) { if (!entry.hadRecentInput) __llShiftScore += entry.value || 0; });
          }).observe({ type: 'layout-shift', buffered: true });
        } catch (_) {}
        // Horizontal overflow is the ONE thing that must be measured at each
        // width, so each is captured at its own moment and stashed. Every
        // other DOM measurement belongs at desktop width — see the note on
        // the phone-width window below.
        function __llOverflowCount() {
          return Array.prototype.filter.call(document.querySelectorAll('body *'), function(el) {
            var r=el.getBoundingClientRect(), cs=getComputedStyle(el);
            return cs.position !== 'fixed' && r.width > 5 && (r.right > document.documentElement.clientWidth + 3 || r.left < -3);
          }).length;
        }
        var __llDesktopOverflow = 0, __llMobileOverflow = 0;
        setTimeout(function() { __llDesktopOverflow = __llOverflowCount(); }, Math.max(300, Math.floor(${timeoutMs} * 0.32)));
        // Captured while the frame is still narrow. The parent widens it
        // again straight after this, so the final report — which measures
        // visibility, overlays, contrast and controls — sees the DESKTOP
        // layout the learner actually gets first.
        if (${responsiveResizeAudit ? 'true' : 'false'}) {
          setTimeout(function() { __llMobileOverflow = __llOverflowCount(); }, Math.max(600, Math.floor(${timeoutMs} * 0.60)));
        }

        // Frozen-animation detection: a canvas that paints once but never
        // actually animates (the exact "generates but nothing visible /
        // nothing moves" complaint) throws no error and has a non-blank
        // canvas, so the existing paintedCanvasCount check alone can't
        // catch it. Wrap requestAnimationFrame to count real animation-loop
        // activity, then checksum each canvas's pixels at an early and a
        // late sample time — if a canvas is being driven by an active rAF
        // loop but its pixels never change between the two samples, the
        // animation is frozen (stuck on a first/only frame, a condition
        // that never becomes true, or math that produces the same output
        // every tick).
        var __rafCalls = 0;
        // FPS estimation: count UNIQUE animation-frame timestamps (multiple
        // rAF loops share one frame, so counting callbacks would overstate
        // the rate — distinct timestamps == distinct rendered frames).
        var __frameCount = 0, __lastTs = -1;
        var __origRAF = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : null;
        if (__origRAF) {
          window.requestAnimationFrame = function(cb) {
            __rafCalls++;
            return __origRAF(function(ts) {
              if (ts !== __lastTs) { __frameCount++; __lastTs = ts; }
              return cb(ts);
            });
          };
        }
        var __fpsSampleStart = null, __fpsFramesAtStart = 0;
        // Reads a canvas's pixels regardless of what kind of rendering
        // context it holds. Per the HTML Canvas spec, once a context has
        // been created via getContext('webgl'/'webgl2'/'bitmaprenderer'),
        // getContext('2d') on that SAME canvas returns null forever after —
        // and that is exactly what a real Three.js scene does
        // (new THREE.WebGLRenderer({ canvas }), see functions/
        // baseScripts.js's 'three' library guide). Calling getContext('2d')
        // directly on such a canvas to read it back, as this used to, always
        // got null and was treated as un-paintable — a genuinely-rendering
        // WebGL/Three.js canvas was misreported as blank, which triggers the
        // repair loop's "Visible canvases appear blank" message (this file's
        // BLOCKING_PROBLEMS) even though nothing is wrong, pushing the model
        // to abandon WebGL/Three.js for plain 2D canvas on retry just to
        // pass this check. drawImage() has no such restriction — it can
        // paint ANY canvas (2d or WebGL-backed) onto a fresh scratch 2D
        // canvas, so read through that instead of the source canvas's own
        // context.
        function __llReadCanvasPixels(canvas) {
          try {
            if (!canvas.width || !canvas.height) return null;
            var ctx = canvas.getContext('2d');
            if (ctx) return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
            var buf = document.createElement('canvas');
            buf.width = canvas.width;
            buf.height = canvas.height;
            var bctx = buf.getContext('2d');
            if (!bctx) return null;
            bctx.drawImage(canvas, 0, 0);
            return bctx.getImageData(0, 0, buf.width, buf.height).data;
          } catch (_) { return null; }
        }
        function __checksumCanvas(canvas) {
          try {
            var data = __llReadCanvasPixels(canvas);
            if (!data) return null;
            var stride = Math.max(4, Math.floor(data.length / 800 / 4) * 4);
            var sum = 0;
            for (var i = 0; i < data.length; i += stride) {
              sum = (sum + data[i] * 31 + data[i + 1] * 17 + data[i + 2] * 7 + data[i + 3]) | 0;
            }
            return sum;
          } catch (_) { return null; }
        }
        // Last known checksum per canvas, refreshed just before controls are
        // exercised so each control is compared against the state left by
        // the one before it rather than all of them against one old sample.
        var __lastCanvasSums = {};
        // Cheap text fingerprint (FNV-1a). Mirrors hashString in
        // src/lib/pageInventory.js — a readout changing from "1.4 s" to
        // "2.1 s" is the clearest possible evidence that a control did
        // something, and it works even when the page has no canvas at all.
        function __llHash(str){
          var s = String(str == null ? '' : str), h = 0x811c9dc5;
          for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
          return h >>> 0;
        }
        function __llTextPrint(){ try { return __llHash(document.body.innerText || ''); } catch (_) { return 0; } }
        var __earlyChecksums = {};
        setTimeout(function() {
          Array.prototype.slice.call(document.querySelectorAll('canvas')).forEach(function(canvas, idx) {
            var sum = __checksumCanvas(canvas);
            if (sum !== null) __earlyChecksums[idx] = sum;
          });
          // Start the FPS sampling window at the same settle point.
          __fpsSampleStart = performance.now();
          __fpsFramesAtStart = __frameCount;
        }, Math.max(250, Math.floor(${timeoutMs} * 0.35)));

        setTimeout(function(){
          try {
            var canvases = Array.prototype.slice.call(document.querySelectorAll('canvas'));
            var report = {
              __llVisualReport: true,
              canvasCount: canvases.length,
              visibleCanvasCount: 0,
              paintedCanvasCount: 0,
              zeroSizeCanvasCount: 0,
              blockingOverlayCount: 0,
              hiddenTopLevelCount: 0,
              frozenAnimatedCanvasCount: 0
              ,inertControlCount: 0
              ,undersizedTouchTargetCount: 0
              ,lowContrastCount: 0
              ,desktopOverflowCount: __llDesktopOverflow
              ,mobileOverflowCount: 0
              ,layoutShiftScore: __llShiftScore
              ,invalidOutputCount: 0
              ,quizMissingScore: false
              ,quizMissingReset: false
              ,fpsEstimate: null
              ,fpsFrames: 0
            };
            // FPS over the sampling window — only meaningful when the page
            // is genuinely running a continuous animation loop.
            if (__fpsSampleStart !== null) {
              var fpsWindowSec = (performance.now() - __fpsSampleStart) / 1000;
              var frames = __frameCount - __fpsFramesAtStart;
              if (fpsWindowSec > 0.4 && frames > 0) {
                report.fpsEstimate = frames / fpsWindowSec;
                report.fpsFrames = frames;
              }
            }
            var __frozenCount = 0;
            // Per-canvas observations for the cross-version record. Same
            // measurements the counts above are derived from — recorded
            // individually rather than summed, because "canvas 2 stopped
            // animating" is actionable and "one fewer animating canvas"
            // is not.
            var __canvasObs = [];
            canvases.forEach(function(canvas, idx){
              var rect = canvas.getBoundingClientRect();
              var visible = rect.width > 20 && rect.height > 20;
              var obs = { visible: visible, painted: false, animated: null };
              __canvasObs.push(obs);
              if (visible) report.visibleCanvasCount += 1;
              if (!canvas.width || !canvas.height) report.zeroSizeCanvasCount += 1;
              if (!visible || !canvas.width || !canvas.height) return;
              try {
                // See __llReadCanvasPixels above — a real WebGL/Three.js
                // canvas can't be read via canvas.getContext('2d') (that
                // returns null once a webgl context already exists on it),
                // so this must not conclude "unpainted" from that null the
                // way it used to.
                var data = __llReadCanvasPixels(canvas);
                if (!data) return;
                var stride = Math.max(4, Math.floor(data.length / 2400 / 4) * 4);
                for (var i = 0; i < data.length; i += stride) {
                  if (data[i + 3] !== 0 && (data[i] !== 0 || data[i + 1] !== 0 || data[i + 2] !== 0)) {
                    report.paintedCanvasCount += 1;
                    obs.painted = true;
                    break;
                  }
                }
              } catch (_) {}
              // Only meaningful if we actually captured an early sample for
              // this canvas AND the page is driving an active rAF loop —
              // a canvas with no animation loop at all is legitimately
              // static and shouldn't be flagged here (that's a design
              // choice, not a bug).
              if (__rafCalls > 2 && typeof __earlyChecksums[idx] === 'number') {
                var laterSum = __checksumCanvas(canvas);
                if (laterSum !== null) {
                  var moved = laterSum !== __earlyChecksums[idx];
                  obs.animated = moved;
                  if (!moved) __frozenCount += 1;
                }
              }
              // A canvas with no rAF loop at all is legitimately static —
              // recorded as null (unknown), never false, so a deliberately
              // still diagram never reads as "the animation stopped".
              __lastCanvasSums[idx] = __checksumCanvas(canvas);
            });
            report.frozenAnimatedCanvasCount = __frozenCount;

            // Controls: direct listeners, inline handlers, native links/forms,
            // or delegated document/window listeners all count as wiring.
            var delegatedTypes = {};
            __llListeners.forEach(function(x){ if(x.target===document || x.target===document.body || x.target===window) delegatedTypes[x.type]=true; });
            var controls = Array.prototype.slice.call(document.querySelectorAll('button,input,select,textarea,[role="button"],[role="slider"]'));
            // Identity of a control, for the cross-version record. HAND-
            // DUPLICATED from controlKey() in src/lib/pageInventory.js
            // because this whole script is a string injected into a
            // sandboxed iframe and cannot import anything (same
            // duplicate-by-necessity as src/lib/sections.js ↔
            // functions/sections.js). The precedence order MUST match that
            // function or the static inventory and this probe will key the
            // same slider differently and every diff becomes noise.
            function __llNorm(t){return String(t||'').normalize('NFC').replace(/<[^>]*>/g,' ').replace(/&(?:#\\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g,' ').replace(/[^\\p{L}\\p{N}\\p{M}]+/gu,' ').trim().toLowerCase().slice(0,40);}
            function __llControlKey(el,tag,type,idx){
              if(el.id)return '#'+el.id;
              if(el.name)return 'name:'+String(el.name).toLowerCase();
              var lbl=__llNorm(el.getAttribute('aria-label')||el.textContent||'');
              if(lbl)return 'label:'+lbl;
              return 'pos:'+tag+':'+(type||'')+':'+idx;
            }
            var __controlObs = {};
            var __textPrint = __llTextPrint();
            controls.forEach(function(el, idx){
              var cs=getComputedStyle(el), r=el.getBoundingClientRect();
              if(cs.display==='none'||cs.visibility==='hidden'||r.width<8||r.height<8||el.disabled)return;
              var tag=el.tagName.toLowerCase(), type=(el.type||'').toLowerCase();
              // Phone-only audit: text fields and range tracks are allowed
              // their natural compact geometry, but pressable/toggle actions
              // need a comfortably tappable hit area.
              var primaryTouchControl=tag==='button'||tag==='a'||el.getAttribute('role')==='button'||(tag==='input'&&/^(button|submit|reset|checkbox|radio)$/.test(type));
              if (${touchTargetAudit ? 'true' : 'false'} && primaryTouchControl && (r.width < 36 || r.height < 36)) report.undersizedTouchTargetCount += 1;
              var nativeAction=(tag==='input' && /^(text|number|checkbox|radio|range|submit|reset)$/.test(type)) || tag==='select' || tag==='textarea' || !!el.closest('form');
              var wired=!!(el.onclick||el.oninput||el.onchange||el.onmousedown||el.onmouseup||el.onpointerdown||el.onpointerup||el.ontouchstart||el.onkeydown) || __llListeners.some(function(x){return x.target===el||x.target.contains&&x.target!==document&&x.target.contains(el);}) || delegatedTypes.click || delegatedTypes.input || delegatedTypes.change || delegatedTypes.mousedown || delegatedTypes.mouseup || delegatedTypes.mousemove || delegatedTypes.keydown;
              if(!nativeAction && !wired) report.inertControlCount += 1;
              // Exercise safe controls so thrown handler errors and layout
              // shifts caused by changing content are observed.
              try {
                if(tag==='input' && type==='range') { var old=el.value; el.value=el.max||String(Number(old||0)+1); el.dispatchEvent(new Event('input',{bubbles:true})); el.dispatchEvent(new Event('change',{bubbles:true})); }
                else if(tag==='button' && type!=='submit') el.click();
              } catch(e) { parent.postMessage({__llErrorReport:true,message:'Control failed: '+String(e.message||e)},'*'); }
              // Did operating it visibly do anything? Evidence is either
              // changed on-screen text, or a changed canvas — but only a
              // canvas that ISN'T animating on its own, since an animating
              // one would have changed regardless and proves nothing.
              var changed = false, couldTell = false;
              var nextText = __llTextPrint();
              if (nextText !== __textPrint) { changed = true; }
              __textPrint = nextText;
              canvases.forEach(function(canvas, ci){
                if (__canvasObs[ci] && __canvasObs[ci].animated === true) return;
                var sum = __checksumCanvas(canvas);
                if (sum === null) return;
                couldTell = true;
                if (__lastCanvasSums[ci] !== undefined && sum !== __lastCanvasSums[ci]) changed = true;
                __lastCanvasSums[ci] = sum;
              });
              // Three-valued on purpose. FALSE is only recorded when the
              // control is also unwired — otherwise "no visible change"
              // may simply mean the handler redraws on the next animation
              // frame, which this synchronous sample cannot see. Guessing
              // false there would invent a regression on a working page,
              // and one false alarm costs more trust than one missed
              // finding costs quality.
              var responsive = changed ? true : ((!nativeAction && !wired) ? false : null);
              __controlObs[__llControlKey(el,tag,type,idx)] = { responsive: responsive, observable: couldTell };
            });

            function rgb(str){var m=String(str).match(/rgba?\\((\\d+)[, ]+(\\d+)[, ]+(\\d+)/);return m?[+m[1],+m[2],+m[3]]:null;}
            function lum(c){return c.map(function(v){v/=255;return v<=.03928?v/12.92:Math.pow((v+.055)/1.055,2.4);}).reduce(function(s,v,i){return s+v*[.2126,.7152,.0722][i];},0);}
            function background(el){while(el){var c=rgb(getComputedStyle(el).backgroundColor);if(c && getComputedStyle(el).backgroundColor.indexOf(', 0)')<0)return c;el=el.parentElement;}return [255,255,255];}
            Array.prototype.forEach.call(document.querySelectorAll('body *'),function(el){
              if(el.children.length && !(el.childNodes.length===1&&el.firstChild.nodeType===3))return;
              if(!(el.textContent||'').trim())return;var cs=getComputedStyle(el),r=el.getBoundingClientRect();if(cs.display==='none'||cs.visibility==='hidden'||r.width<2||r.height<2)return;
              var fg=rgb(cs.color),bg=background(el);if(!fg)return;var a=lum(fg),b=lum(bg),ratio=(Math.max(a,b)+.05)/(Math.min(a,b)+.05);var large=parseFloat(cs.fontSize)>=18||(parseFloat(cs.fontSize)>=14&&parseInt(cs.fontWeight)>=700);if(ratio<(large?3:4.5))report.lowContrastCount+=1;
            });
            report.mobileOverflowCount=__llMobileOverflow;
            var visibleText=(document.body.innerText||'');
            report.invalidOutputCount=(visibleText.match(/\\b(?:NaN|undefined|Infinity)\\b/g)||[]).length;
            var quizLike=/\\b(quiz|question\\s*\\d*|check answer|your score)\\b/i.test(visibleText);
            if(quizLike){report.quizMissingScore=!/\\b(score|points?|\\d+\\s*\\/\\s*\\d+|progress)\\b/i.test(visibleText);var reset=Array.prototype.find.call(document.querySelectorAll('button,[role="button"]'),function(el){return /reset|restart|try again|new quiz/i.test((el.textContent||'')+' '+(el.getAttribute('aria-label')||''));});report.quizMissingReset=!reset;}

            // Stuck full-viewport overlay detection: an element covering
            // almost the whole page, positioned fixed/absolute, sitting
            // above other content (high z-index or simply later in DOM
            // order with a non-transparent background), and with no
            // reachable close/dismiss control inside it.
            var vw = window.innerWidth, vh = window.innerHeight;
            var all = Array.prototype.slice.call(document.querySelectorAll('body *'));
            var closeWordRe = /close|dismiss|got it|continue|try again|\\u00d7|x/i;
            all.forEach(function(el){
              var cs = window.getComputedStyle(el);
              if (cs.position !== 'fixed' && cs.position !== 'absolute') return;
              var rect = el.getBoundingClientRect();
              var coversViewport = rect.width >= vw * 0.9 && rect.height >= vh * 0.9;
              if (!coversViewport) return;
              var bg = cs.backgroundColor || '';
              var hasVisibleBg = bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)';
              if (!hasVisibleBg) return;
              if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return;
              var hasCloseControl = false;
              var controls = el.querySelectorAll('button, [role="button"], a, [onclick]');
              for (var j = 0; j < controls.length; j++) {
                if (closeWordRe.test(controls[j].textContent || '') || closeWordRe.test(controls[j].getAttribute('aria-label') || '')) {
                  hasCloseControl = true;
                  break;
                }
              }
              if (!hasCloseControl) report.blockingOverlayCount += 1;
            });

            // Hidden-top-level-content detection: direct children of body
            // (the page's main sections) that are invisible by default
            // with no obvious toggle mechanism nearby. Skip tiny/utility
            // elements (scripts, tooltips under ~2px) to avoid false
            // positives on legitimate zero-size helper nodes.
            var topLevel = Array.prototype.slice.call(document.body.children);
            topLevel.forEach(function(el){
              var tag = el.tagName.toLowerCase();
              if (tag === 'script' || tag === 'style' || tag === 'template' || tag === 'noscript') return;
              var cs = window.getComputedStyle(el);
              var rect = el.getBoundingClientRect();
              var isHidden = cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0;
              var isZeroSize = rect.width <= 1 && rect.height <= 1;
              if (isHidden || isZeroSize) report.hiddenTopLevelCount += 1;
            });

            // ── The cross-version observation record ──────────────────
            // Everything above this line answers "what's wrong with this
            // page". This answers "what can this page do", which is the
            // only question that makes the NEXT generation's regressions
            // visible. See src/lib/pageDiff.js.
            var __stages = 0;
            try {
              // The base stylesheet's step-through row renders "Step 2 / 5"
              // in .ll-step-count (see functions/baseStyles.js and the
              // STEP-THROUGH SEQUENCES rule in functions/systemPrompt.js).
              var stepEl = document.querySelector('.ll-step-count');
              var stepMatch = stepEl && (stepEl.textContent || '').match(/(\\d+)\\s*(?:\\/|of)\\s*(\\d+)/i);
              if (stepMatch) __stages = parseInt(stepMatch[2], 10) || 0;
              else {
                var stepper = document.querySelector('.ll-stepper');
                if (stepper) __stages = stepper.querySelectorAll('button').length;
              }
            } catch (_) {}

            report.observations = {
              probeVersion: 1,
              canvases: __canvasObs,
              controls: __controlObs,
              stages: __stages,
              quiz: {
                present: quizLike,
                hasScore: quizLike ? !report.quizMissingScore : null,
                hasReset: quizLike ? !report.quizMissingReset : null
              }
            };

            parent.postMessage(report, '*');
          } catch (e) {
            parent.postMessage({__llErrorReport:true,message:String(e && e.message || e)}, '*');
          }
        }, Math.max(500, ${Math.max(500, timeoutMs - 400)}));
      </script>`;
}
